import React from 'react'
import { utilService, otaReservationService } from '../../core/services'
import { IJsonResponse } from '../../core/JsonApiSerializer'
import { IGuestEmail, IMeetupPlace, IOtaReservation, IPost, IWindow } from '../../static/interfaces'
import { useGuestEditor, IOtaReservationGuestEditor } from './useGuestEditor'
import { useTourEditor, IOtaReservationTourEditor } from './useTourEditor'
import { usePlaceEditor, IOtaReservationPlaceEditor } from './usePlaceEditor'
import {
  IOtaReservationEditPermissions,
  IOtaReservationEditPermissionsResponse,
  IOtaReservationMemoUpdateParams,
  formatEditPermissions,
} from './types'

declare var window: IWindow
export interface IOtaReservationProps {
  ota_reservation: IJsonResponse
  user: IJsonResponse
  post: IJsonResponse
  guest_emails: IJsonResponse
  meetup_places: IJsonResponse
  posts: IJsonResponse
  edit_permissions: IOtaReservationEditPermissionsResponse
}

interface IOtaReservationEditor {
  guest: IOtaReservationGuestEditor
  tour: IOtaReservationTourEditor
  place: IOtaReservationPlaceEditor
  resetEditValues: () => void
  saveEditValues: () => Promise<boolean>
  isSaving: boolean
  canEdit: boolean
}
export interface IOtaReservationHookResult {
  otaReservation: IOtaReservation
  editor: IOtaReservationEditor
}

/**
 * JSONから取得したデータをフォーマットし一括で返す
 * @param props
 * @returns
 */
function getFormattedDataFromJson(props: IOtaReservationProps) {
  const { data: initialOtaReservation } = utilService.getDataFromJson(props.ota_reservation) as {
    data: IOtaReservation
  }
  const { data: post } = utilService.getDataFromJson(props.post) as { data: IPost | null }

  const { data: guest_emails } = utilService.getDataFromJson(props.guest_emails) as {
    data: IGuestEmail[]
  }
  const { data: meetupPlaces } = utilService.getDataFromJson(props.meetup_places) as {
    data: IMeetupPlace[]
  }
  const { data: posts } = utilService.getDataFromJson(props.posts) as { data: IPost[] }
  const editPermissions: IOtaReservationEditPermissions = formatEditPermissions(
    props.edit_permissions
  )

  return {
    initialOtaReservation,
    post,
    guest_emails,
    meetupPlaces,
    posts,
    editPermissions,
  }
}

/**
 * otaReservation用のカスタムフック
 * 編集用のエディタを含むOTA予約情報を返す
 * @param props
 * @returns
 */
export const useOtaReservation = (props: IOtaReservationProps): IOtaReservationHookResult => {
  const {
    initialOtaReservation,
    post,
    guest_emails,
    meetupPlaces,
    posts,
    editPermissions,
  } = getFormattedDataFromJson(props)

  const [otaReservation, setOtaReservation] = React.useState<IOtaReservation>({
    ...initialOtaReservation,
    post: initialOtaReservation.post ?? post ?? null,
  })

  const guest = useGuestEditor(otaReservation, guest_emails, editPermissions)
  const tour = useTourEditor(otaReservation, posts, post, editPermissions)
  const place = usePlaceEditor(otaReservation, meetupPlaces, editPermissions)
  const [isSaving, setIsSaving] = React.useState(false)
  const canEdit = editPermissions.canUpdateOtaReservation || editPermissions.canUpdateMemo
  const resetEditValues = () => {
    guest.reset()
    tour.reset()
    place.reset()
  }

  /**
   * フォームごとに分かれていた変更保存が一つのボタンからに統合されたことによる集約関数
   */
  const saveEditValues = async (): Promise<boolean> => {
    const error = place.validate()
    if (error) {
      window.flashMessages.addMessage({ text: error, type: 'error' })
      return false
    }

    const savedSections: string[] = []
    setIsSaving(true)

    // エラー検知がPromise.allだとどのセクションで失敗したか特定しにくく、
    // ツアー情報はポストや催行時間に影響するため先に保存する必要があり、
    // Promise.allSettledを使うとes2020以上を設定するように警告が出たため並列実行していない
    try {
      const updateParams = { ...guest.buildUpdateParams(), ...place.buildUpdateParams() }
      const memoUpdateParams: IOtaReservationMemoUpdateParams = guest.buildMemoUpdateParams()
      const tourResult = await tour.save(setOtaReservation)
      if (tourResult.canceled) {
        return false
      }
      let updatedOtaReservation = tourResult.value
      setOtaReservation(updatedOtaReservation)
      if (tourResult.saved) {
        savedSections.push('ツアー情報')
      }

      if (Object.keys(updateParams).length > 0) {
        const response = await otaReservationService.update(otaReservation.id, updateParams)
        updatedOtaReservation = response.updatedOtaReservation as IOtaReservation
        setOtaReservation(updatedOtaReservation)
        savedSections.push('OTA予約情報')
      }

      if (Object.keys(memoUpdateParams).length > 0) {
        const response = await otaReservationService.updateMemo(otaReservation.id, memoUpdateParams)
        updatedOtaReservation = response.updatedOtaReservation as IOtaReservation
        setOtaReservation(updatedOtaReservation)
        savedSections.push('メモ')
      }

      const guestEmailResult = await guest.saveGuestEmails()
      const emails = guestEmailResult.value
      if (guestEmailResult.saved) {
        savedSections.push('CCメール')
      }

      guest.reset(updatedOtaReservation, emails)
      tour.reset(updatedOtaReservation)
      place.reset(updatedOtaReservation)
      window.flashMessages.addMessage({ text: '更新しました', type: 'success' })
      return true
    } catch (e) {
      console.error(e)
      if (savedSections.length > 0) {
        window.flashMessages.addMessage({
          text: `${savedSections.join('・')}を更新しました`,
          type: 'success',
        })
      }

      return false
    } finally {
      setIsSaving(false)
    }
  }

  return {
    otaReservation,
    editor: {
      guest: guest.editor,
      tour: tour.editor,
      place: place.editor,
      resetEditValues,
      saveEditValues,
      isSaving,
      canEdit,
    },
  }
}
